import React from "react";
import {css, Theme} from "@emotion/react";

import {getOfferListUrl} from "../../list/url_utils/get_offer_list_url";

type OfferRelatedLinksProps = {
    make?: string;
    model?: string;
    city?: string;
    voivodeship?: string;
};

const PL_TO_ASCII: Record<string, string> = {
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ó: "o",
    ś: "s",
    ź: "z",
    ż: "z"
};

const toSlug = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .split("")
        .map((ch) => PL_TO_ASCII[ch] || ch)
        .join("")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const makePathSlug = (make: string): string => {
    const raw = make.trim().toLowerCase();
    if (raw === "mercedes-benz" || raw === "mercedesbenz") {
        return "mercedesbenz";
    }
    return toSlug(make);
};

/**
 * Internal SEO links on offer detail — same visual language as offer sections
 * (hairline top border, quiet text links, brand hover).
 */
export const OfferRelatedLinks = ({make, model, city, voivodeship}: OfferRelatedLinksProps) => {
    const makeSlug = make ? makePathSlug(make) : "";
    const citySlug = city ? toSlug(city) : "";
    const regionSlug = voivodeship ? toSlug(voivodeship) : "";

    const links: Array<{href: string; label: string}> = [];

    if (makeSlug) {
        links.push({
            href: getOfferListUrl({offerType: "osobowe", type: makeSlug as any}),
            label: `Więcej aut ${make}`
        });
    }

    if (makeSlug && regionSlug && citySlug) {
        links.push({
            href: getOfferListUrl({
                offerType: "osobowe",
                type: makeSlug as any,
                location: regionSlug,
                subLocation: citySlug
            }),
            label: `${make} w mieście ${city}`
        });
    }

    if (regionSlug && citySlug) {
        links.push({
            href: getOfferListUrl({
                offerType: "osobowe",
                location: regionSlug,
                subLocation: citySlug
            }),
            label: `Samochody w ${city}`
        });
    }

    if (makeSlug && model) {
        links.push({
            href: getOfferListUrl({
                offerType: "osobowe",
                type: makeSlug as any,
                model
            }),
            label: `Inne ${make} ${model}`
        });
    }

    if (!links.length) {
        return null;
    }

    return (
        <section css={sectionStyle}>
            <h2 css={headingStyle}>Zobacz też</h2>
            <p css={leadStyle}>Powiązane ogłoszenia i listy — łatwiej znajdziesz podobne auto.</p>
            <ul css={listStyle}>
                {links.map((link) => (
                    <li key={link.href + link.label} css={itemStyle}>
                        <a href={link.href} css={linkStyle}>
                            {link.label}
                        </a>
                    </li>
                ))}
            </ul>
        </section>
    );
};

const sectionStyle = css`
    padding: 24px 0 48px 0;
    border-top: 1px solid #dcdde0;
`;

const headingStyle = css`
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 600;
    color: #333;
`;

const leadStyle = css`
    margin: 0 0 20px;
    font-size: 15px;
    line-height: 1.45;
    color: #66676c;
    max-width: 42rem;
`;

const listStyle = css`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 10px 12px;
`;

const itemStyle = css`
    margin: 0;
`;

const linkStyle = (theme: Theme) => css`
    display: inline-block;
    padding: 8px 14px;
    border: 1px solid #dcdde0;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.3;
    color: #333;
    text-decoration: none;
    background: #fff;
    transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
        color: ${theme.colors.brand_primary};
        border-color: ${theme.colors.brand_primary};
        background: #fff7f2;
        text-decoration: none;
    }
`;
